// Spec: VMHAEventsTable.spec.md

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ApiClient from 'api-client/ApiClient'
import { useAppSelector } from 'app/store'
import SimpleLink from 'core/components/SimpleLink'
import TimePicklist, { TIME_RANGES } from 'core/components/widgets/TimePicklist'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import { routeManager } from 'core/utils/routes'
import { listResmgrHosts } from '../../actions'
import { resmgrHostsById } from '../../selectors'
import { IHostDetailsPageTabs } from '../host-details/model'
import VMHADetailsDrawer from '../host-details/VMHADetailsDrawer'
import { getTimeRangeFromString } from './timeHelpers'
import { VMHAEvent, VMHAHostStatus } from './types'
import {
  createVMHAHostStatus,
  formatVMHATimestamp,
  getFirstEventForHost,
  getLatestEventByHost,
  parseVMHAEventsResponse,
  parseVMHATaskDetailsResponse,
} from './vmhaHelpers'

interface VMHAEventsTableProps {
  hostId?: string
  filteredHosts?: Array<{ id: string; name?: string; clusterName?: string }>
  hostsLoading?: boolean
}

const { vmha } = ApiClient.getInstance()

// FR-008: Search targets for ListContainer search
const searchTargets = ['vmhaUuid', 'instanceName', 'cluster']
const searchTargetLabels = { vmhaUuid: 'Event', instanceName: 'Host', cluster: 'Cluster' }
const columnsOrder = ['vmhaUuid', 'instanceName', 'hostId', 'cluster', 'timestamp', 'actions']

const VMHAEventsTable: React.FC<VMHAEventsTableProps> = ({
  hostId,
  filteredHosts,
  hostsLoading = false,
}) => {
  const [loading, setLoading] = useState(false)
  const [allEvents, setAllEvents] = useState<VMHAEvent[]>([])
  const [selectedEvent, setSelectedEvent] = useState<VMHAHostStatus | undefined>(undefined)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [showRetry, setShowRetry] = useState(false)
  const [loadingDetails, setLoadingDetails] = useState(false)
  // FR-005: Default time range is last_7_days
  const [selectedTimeRange, setSelectedTimeRange] = useState(TIME_RANGES.last_7_days)

  useListAction(listResmgrHosts)
  const resMgrHosts = useAppSelector(resmgrHostsById)

  // FR-009: Store raw events separately for two-phase processing
  const [rawEventsByHost, setRawEventsByHost] = useState<Map<string, any[]>>(new Map())

  // FR-001, FR-002: Fetch events — API call depends on hostId presence
  const fetchEvents = useCallback(async () => {
    if (hostId === '') return

    try {
      setLoading(true)
      const { start, end } = getTimeRangeFromString(selectedTimeRange)
      const response = hostId
        ? await vmha.getVMHAEventsForHost(hostId, start, end)
        : await vmha.getCompletedVMHAEventsForHost(start, end)
      setRawEventsByHost(parseVMHAEventsResponse(response))
    } catch (error) {
      console.error('Failed to fetch VMHA events:', error)
      setRawEventsByHost(new Map())
    } finally {
      setLoading(false)
    }
  }, [hostId, selectedTimeRange])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  // FR-009: Resolve host names when resMgrHosts data becomes available
  useEffect(() => {
    let aggregated: VMHAEvent[] = []
    rawEventsByHost.forEach((hostEvents) => {
      if (!hostEvents || hostEvents.length === 0) return
      const events = getFirstEventForHost(hostEvents, resMgrHosts)
      if (events?.length > 0) aggregated = aggregated.concat(events)
    })
    aggregated.sort((a, b) => b.eventTimestamp - a.eventTimestamp)
    setAllEvents(aggregated)
  }, [rawEventsByHost, resMgrHosts])

  // FR-007: Track latest event per host for conditional retry
  const latestEventByHost = useMemo(() => getLatestEventByHost(allEvents), [allEvents])

  // FR-003: Filter events by host IDs from filteredHosts (all-hosts mode only)
  const displayedEvents: VMHAEvent[] = useMemo(() => {
    if (hostId) return allEvents
    if (!filteredHosts?.length) return []
    const hostIds = new Set(filteredHosts.map((h) => h.id))
    return allEvents.filter((event) => hostIds.has(event.hostId))
  }, [allEvents, filteredHosts, hostId])

  // FR-006: View Details opens drawer with task details
  // FR-007: Only allow retry for latest event per host
  const handleViewDetails = useCallback(
    async (event: VMHAEvent) => {
      try {
        setLoadingDetails(true)
        setDrawerOpen(true)
        setShowRetry(latestEventByHost.get(event.hostId) === event.vmhaUuid)
        const taskDetailsResponse = await vmha.getVMHATaskDetails(
          event.vmhaUuid,
          event.eventTimestamp,
        )
        const tasks = parseVMHATaskDetailsResponse(taskDetailsResponse)
        const hostStatus = createVMHAHostStatus(event, tasks)
        setSelectedEvent(hostStatus)
      } catch (error) {
        console.error('Failed to fetch VMHA task details:', error)
      } finally {
        setLoadingDetails(false)
      }
    },
    [latestEventByHost],
  )

  // FR-004: Table columns — Event UUID, Host Name, Cluster, Timestamp, View Details
  const columns: GridViewColumn<VMHAEvent>[] = useMemo(
    () => [
      {
        key: 'vmhaUuid',
        label: 'Event',
        CellComponent: ({ value }: { value: string }) => <Text variant="body2">{value}</Text>,
      },
      {
        key: 'instanceName',
        label: 'Host',
        CellComponent: ({ value, item }: { value: string; item: VMHAEvent }) => (
          <SimpleLink
            src={routeManager.routes.openstack.hostDetails.path({
              id: item.hostId,
              tab: IHostDetailsPageTabs.Overview,
            })}
          >
            {value || item.hostId}
          </SimpleLink>
        ),
      },
      {
        key: 'cluster',
        label: 'Cluster',
        CellComponent: ({ value }: { value: string }) =>
          value ? (
            <SimpleLink src={routeManager.routes.openstack.clusterDetails.path({ id: value })}>
              {value}
            </SimpleLink>
          ) : (
            <Text variant="body2">—</Text>
          ),
      },
      {
        key: 'timestamp',
        label: 'Start Timestamp',
        CellComponent: ({ value }: { value: string }) => (
          <Text variant="body2">{formatVMHATimestamp(value)}</Text>
        ),
      },
      {
        key: 'actions',
        label: 'Actions',
        CellComponent: ({ item }: { item: VMHAEvent }) => (
          <SimpleLink onClick={() => handleViewDetails(item)}>View Details</SimpleLink>
        ),
      },
    ],
    [handleViewDetails],
  )

  // FR-005: Time range change handler
  const handleTimeRangeChange = useCallback(
    (timeRange: string, _startTimeEpoch: number, _endTimeEpoch: number, _steps: string) => {
      setSelectedTimeRange(timeRange)
    },
    [],
  )

  const reloadAction = useCallback(
    (_hostId: string) => {
      if (selectedEvent?.event) {
        handleViewDetails(selectedEvent.event)
      }
    },
    [selectedEvent, handleViewDetails],
  )

  return (
    <>
      <ListContainer<any, any>
        searchTargets={searchTargets}
        searchTargetLabels={searchTargetLabels}
        disableSearch={!!hostId}
        hideHeaderPortals={!!hostId}
        visibleColumns={columnsOrder}
        columnsOrder={columnsOrder}
        label="VM High Availability Events"
        data={displayedEvents}
        columns={columns}
        loading={loading || hostsLoading}
        uniqueIdentifier="vmhaUuid"
        emptyContent="No VMHA events found"
        onRefresh={!!hostId ? null : fetchEvents}
        showBreadcrumbs={false}
        extraToolbarContent={
          <TimePicklist onChange={handleTimeRangeChange} currentTimeRange={selectedTimeRange} />
        }
        disableRowSelection
      />
      {/* FR-010: Drawer close resets showRetry */}
      {drawerOpen && (
        <VMHADetailsDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false)
            setSelectedEvent(undefined)
            setShowRetry(false)
          }}
          vmhaStatus={selectedEvent}
          loading={loadingDetails}
          afterRetry={reloadAction}
          showRetry={showRetry}
        />
      )}
    </>
  )
}

export default VMHAEventsTable
